import { Locale } from '@mws/wordengine-graph';
import { useRouter } from 'next/router';
import { createContext, Dispatch, useReducer } from 'react';
import { ValueOf } from 'ts-essentials';

export enum Namespace {
    mrswordsmith = 'mrswordsmith',
    mcgrawhill = 'mcgrawhill'
}

export enum ContentVersion {
    live = 'Live',
    edit = 'Edit'
}

export enum ContextLocale {
    UK = Locale.UK,
    US = Locale.US
}

export const NamespaceColor = {
    [Namespace.mrswordsmith]: '#9fcee5',
    [Namespace.mcgrawhill]: '#d18089'
} as const;

enum ActionType {
    CHANGE_NAMESPACE,
    CHANGE_LOCALE,
    CHANGE_CONTENT_VERSION
}

interface State {
    namespace: Namespace;
    namespaceColor: ValueOf<typeof NamespaceColor>;
    locale: Locale;
    contentVersion: ContentVersion;
}

export interface IGlobalSettingsContext {
    state: State;
    dispatch: Dispatch<IChangeNamespace | IChangeLocale | IChangeContentVersion>;
}

interface IChangeNamespace {
    type: ActionType.CHANGE_NAMESPACE;
    payload: Namespace;
}

interface IChangeLocale {
    type: ActionType.CHANGE_LOCALE;
    payload: Locale;
}

interface IChangeContentVersion {
    type: ActionType.CHANGE_CONTENT_VERSION;
    payload: ContentVersion;
}

const GlobalSettingsContext = createContext<IGlobalSettingsContext>({
    state: {
        namespace: Namespace.mrswordsmith,
        namespaceColor: NamespaceColor[Namespace.mrswordsmith],
        locale: Locale.UK,
        contentVersion: ContentVersion.live
    },
    dispatch: () => null
});

const ChangeNamespace = (value: Namespace): IChangeNamespace => ({
    type: ActionType.CHANGE_NAMESPACE,
    payload: value
});

const ChangeLocale = (value: Locale): IChangeLocale => ({
    type: ActionType.CHANGE_LOCALE,
    payload: value
});

const ChangeContentVersion = (value: ContentVersion): IChangeContentVersion => ({
    type: ActionType.CHANGE_CONTENT_VERSION,
    payload: value
});

const reducer = (
    state: State,
    action: IChangeNamespace | IChangeLocale | IChangeContentVersion
): State => {
    switch (action.type) {
        case ActionType.CHANGE_NAMESPACE:
            return {
                ...state,
                namespace: action.payload,
                namespaceColor: NamespaceColor[action.payload]
            };
        case ActionType.CHANGE_LOCALE:
            return {
                ...state,
                locale: action.payload
            };
        case ActionType.CHANGE_CONTENT_VERSION:
            return {
                ...state,
                contentVersion: action.payload
            };
        default:
            return state;
    }
};

const GlobalSettingsProvider = ({ children }: { children: JSX.Element }) => {
    const router = useRouter();
    let namespace = router.query.namespace as string;
    let locale = router.query.locale as string;

    if (!namespace && !(namespace in Namespace)) {
        namespace = Namespace.mrswordsmith;
    }

    if (!locale && !(locale in Object.keys(ContextLocale))) {
        locale = Locale.UK;
    }

    const initialState: State = {
        namespace: Namespace[namespace],
        namespaceColor: NamespaceColor[Namespace[namespace]],
        locale: ContextLocale[locale],
        contentVersion: ContentVersion.live
    };
    const [state, dispatch] = useReducer(reducer, initialState);

    return (
        <GlobalSettingsContext.Provider value={{ state, dispatch }}>
            {children}
        </GlobalSettingsContext.Provider>
    );
};

export {
    GlobalSettingsContext,
    GlobalSettingsProvider,
    ChangeNamespace,
    ChangeLocale,
    ChangeContentVersion
};
